import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Wallet, TrendingDown, ArrowUpRight, ArrowDownRight, AlertTriangle, BarChart3, Banknote, ShieldCheck, ShieldAlert, Percent, CreditCard, Smartphone, Store, Truck, RefreshCw, Loader2, Package, ShoppingBag, List, PieChart, FileText, Calendar, Tag, User, Clock } from 'lucide-react';
import { accountsAPI } from '../lib/api';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { format, subDays, startOfMonth } from 'date-fns';
import PeriodToolbar from '../components/PeriodToolbar';
import { usePeriod } from '../hooks/usePeriod';
import { PERIOD_MODES } from '../lib/period';

const fmtCur = (v) => {
  if (v === null || v === undefined) return '--';
  const abs = Math.abs(v);
  if (abs >= 100000) return `${v < 0 ? '-' : ''}₹${(abs / 100000).toFixed(2)}L`;
  if (abs >= 1000) return `${v < 0 ? '-' : ''}₹${(abs / 1000).toFixed(1)}K`;
  return `₹${v.toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
};

const StatCard = ({ label, value, icon: Icon, color = 'text-zinc-200', sub, tooltip, testId }) => (
  <Card className="bg-zinc-900/40 border-zinc-800/40" data-testid={testId}>
    <CardContent className="p-3">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-[10px] text-zinc-500 uppercase tracking-wider flex items-center gap-1" title={tooltip || undefined}>
            {label}
            {tooltip && <span className="text-[9px] text-zinc-600" aria-label="info">&#9432;</span>}
          </p>
          <p className={`text-lg font-semibold mt-0.5 ${color}`}>{value}</p>
          {sub && <p className="text-[10px] text-zinc-500 mt-0.5">{sub}</p>}
        </div>
        {Icon && <Icon className={`w-4 h-4 ${color} opacity-50`} />}
      </div>
    </CardContent>
  </Card>
);

// EC's closing balance is a legacy observation, not cash truth. A difference
// against it is NOT a reconciliation, variance, shortage or excess — the real
// cash control starts when 1OS Bills supplies a physical cash count.
const LEGACY_OBS_STYLES = {
  NO_DIFFERENCE: { color: 'text-zinc-300', bg: 'bg-zinc-800/40 border-zinc-700/40', label: 'Legacy EC observation agrees' },
  DIFFERENCE_PRESENT: { color: 'text-zinc-300', bg: 'bg-zinc-800/40 border-zinc-700/40', label: 'Legacy EC observation differs' },
  UNKNOWN_OPENING: { color: 'text-zinc-500', bg: 'bg-zinc-800/40 border-zinc-700/40', label: 'Opening cash unknown' },
  NO_LEGACY_OBSERVATION: { color: 'text-zinc-500', bg: 'bg-zinc-800/40 border-zinc-700/40', label: 'No legacy EC observation' },
};

const getLegacyObsStyle = (status) => LEGACY_OBS_STYLES[status] || LEGACY_OBS_STYLES.NO_LEGACY_OBSERVATION;

const CLASSIFICATION_COLORS = {
  INVENTORY_VIA_CASH: { bg: 'bg-amber-500/10', text: 'text-amber-400', border: 'border-amber-500/30' },
  CASH_OUTFLOW: { bg: 'bg-rose-500/10', text: 'text-rose-400', border: 'border-rose-500/30' },
  INVENTORY: { bg: 'bg-blue-500/10', text: 'text-blue-400', border: 'border-blue-500/30' },
  OTHER: { bg: 'bg-zinc-500/10', text: 'text-zinc-400', border: 'border-zinc-500/30' },
};

const ClassificationBadge = ({ classification }) => {
  const style = CLASSIFICATION_COLORS[classification] || CLASSIFICATION_COLORS.OTHER;
  return (
    <span className={`inline-flex px-1.5 py-0.5 rounded text-[9px] font-medium ${style.bg} ${style.text} ${style.border} border`} data-testid={`cls-badge-${classification}`}>
      {classification}
    </span>
  );
};

const TABS = [
  { key: 'summary', label: 'Summary', icon: BarChart3 },
  { key: 'ledger', label: 'Ledger', icon: List },
  { key: 'breakdown', label: 'Breakdown', icon: PieChart },
];

const RAIL_META = {
  CASH: { label: 'Cash', color: 'text-emerald-400', bg: 'bg-emerald-500/10', icon: Banknote },
  UPI: { label: 'UPI', color: 'text-blue-400', bg: 'bg-blue-500/10', icon: Smartphone },
  CARD: { label: 'Card', color: 'text-violet-400', bg: 'bg-violet-500/10', icon: CreditCard },
  SWIGGY_PAY: { label: 'Swiggy', color: 'text-orange-400', bg: 'bg-orange-500/10', icon: Truck },
  ZOMATO_PAY: { label: 'Zomato', color: 'text-red-400', bg: 'bg-red-500/10', icon: Store },
};
const getRailMeta = (rail) => RAIL_META[rail] || { label: rail, color: 'text-zinc-400', bg: 'bg-zinc-500/10', icon: Wallet };

// ═══════════════════════════════════════════════════
// LEDGER TAB
// ═══════════════════════════════════════════════════
function LedgerTab({ dateFrom, dateTo }) {
  const [ledger, setLedger] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    accountsAPI.getExpenseLedger({ date_from: dateFrom, date_to: dateTo, group_by: 'date' })
      .then(r => setLedger(r.data))
      .catch(() => toast.error('Failed to load expense ledger'))
      .finally(() => setLoading(false));
  }, [dateFrom, dateTo]);

  if (loading) return <div className="flex items-center justify-center py-12"><Loader2 className="w-5 h-5 animate-spin text-zinc-500" /></div>;
  if (!ledger || ledger.total_entries === 0) {
    return (
      <Card className="bg-zinc-900/40 border-zinc-800/40">
        <CardContent className="p-8 text-center">
          <FileText className="w-8 h-8 text-zinc-600 mx-auto mb-3" />
          <p className="text-sm text-zinc-400">No expense entries for this period</p>
          <p className="text-[11px] text-zinc-600 mt-1">Sync expenses from the Expense Engine to populate the ledger</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4" data-testid="ledger-tab">
      {/* Ledger KPI strip */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard label="Total Entries" value={ledger.total_entries} icon={FileText} color="text-zinc-200" />
        <StatCard label="Total Amount" value={fmtCur(ledger.total_amount)} icon={Banknote} color="text-zinc-200" />
        <StatCard label="Cash Outflows" value={fmtCur(ledger.cash_outflow_total)} icon={ArrowDownRight} color="text-rose-400" />
        <StatCard label="Inventory via Cash" value={fmtCur(ledger.inventory_via_cash_total)} icon={Package} color="text-amber-400" />
      </div>

      {/* Day-wise grouped ledger */}
      {ledger.groups.map(group => (
        <Card key={group.key} className="bg-zinc-900/40 border-zinc-800/40" data-testid={`ledger-group-${group.key}`}>
          <CardHeader className="pb-1 pt-3 px-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Calendar className="w-3.5 h-3.5 text-zinc-500" />
                <CardTitle className="text-xs font-semibold text-zinc-300">{group.key}</CardTitle>
                <Badge variant="outline" className="text-[9px] border-zinc-700 text-zinc-500">{group.count} entries</Badge>
              </div>
              <div className="flex items-center gap-3 text-[10px]">
                <span className="text-zinc-400">Subtotal: <span className="font-mono text-zinc-200">{fmtCur(group.subtotal)}</span></span>
                {group.inventory_via_cash > 0 && <span className="text-amber-400 font-mono">Inv: {fmtCur(group.inventory_via_cash)}</span>}
                {group.cash_outflow > 0 && group.cash_outflow !== group.subtotal && <span className="text-rose-400 font-mono">Cash: {fmtCur(group.cash_outflow)}</span>}
              </div>
            </div>
          </CardHeader>
          <CardContent className="pt-0 px-4 pb-3">
            <div className="overflow-x-auto">
              <table className="w-full text-[11px]">
                <thead>
                  <tr className="border-b border-zinc-800 text-zinc-500">
                    <th className="text-left py-1.5 pr-2 font-medium">Time</th>
                    <th className="text-left py-1.5 pr-2 font-medium">Type</th>
                    <th className="text-left py-1.5 pr-2 font-medium">Category</th>
                    <th className="text-left py-1.5 pr-2 font-medium">Vendor / Payee</th>
                    <th className="text-left py-1.5 pr-2 font-medium">Description</th>
                    <th className="text-right py-1.5 pr-2 font-medium">Amount</th>
                    <th className="text-left py-1.5 pr-2 font-medium">Classification</th>
                    <th className="text-left py-1.5 font-medium">Ref</th>
                  </tr>
                </thead>
                <tbody>
                  {group.entries.map((entry, idx) => (
                    <tr key={entry.expense_id || idx} className="border-b border-zinc-800/30 hover:bg-zinc-800/20 transition-colors" data-testid={`ledger-row-${entry.expense_id}`}>
                      <td className="py-1.5 pr-2 text-zinc-500 font-mono">{entry.time || '--'}</td>
                      <td className="py-1.5 pr-2 text-zinc-400">{entry.expense_type || '--'}</td>
                      <td className="py-1.5 pr-2 text-zinc-300">{entry.category || '--'}</td>
                      <td className="py-1.5 pr-2 text-zinc-300">{entry.vendor_name || '--'}</td>
                      <td className="py-1.5 pr-2 text-zinc-400 max-w-[200px] truncate">{entry.description || '--'}</td>
                      <td className="py-1.5 pr-2 text-right font-mono font-medium text-zinc-200">{fmtCur(entry.amount)}</td>
                      <td className="py-1.5 pr-2"><ClassificationBadge classification={entry.classification} /></td>
                      <td className="py-1.5 text-zinc-600 font-mono text-[9px]">{(entry.expense_id || '').slice(0, 8)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

// ═══════════════════════════════════════════════════
// BREAKDOWN TAB
// ═══════════════════════════════════════════════════
function BreakdownTab({ dateFrom, dateTo }) {
  const [ledger, setLedger] = useState(null);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('category');

  useEffect(() => {
    setLoading(true);
    accountsAPI.getExpenseLedger({ date_from: dateFrom, date_to: dateTo, group_by: view })
      .then(r => setLedger(r.data))
      .catch(() => toast.error('Failed to load breakdown'))
      .finally(() => setLoading(false));
  }, [dateFrom, dateTo, view]);

  const VIEWS = [
    { key: 'category', label: 'By Category', icon: Tag },
    { key: 'vendor', label: 'By Vendor', icon: User },
    { key: 'expense_type', label: 'By Type', icon: FileText },
    { key: 'date', label: 'By Date', icon: Calendar },
  ];

  if (loading) return <div className="flex items-center justify-center py-12"><Loader2 className="w-5 h-5 animate-spin text-zinc-500" /></div>;

  const breakdown = ledger?.breakdown || {};
  const groups = ledger?.groups || [];
  const total = ledger?.total_amount || 1;

  return (
    <div className="space-y-4" data-testid="breakdown-tab">
      {/* View selector */}
      <div className="flex gap-1.5">
        {VIEWS.map(v => (
          <button key={v.key} onClick={() => setView(v.key)}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs transition-colors ${
              view === v.key ? 'bg-zinc-700 text-zinc-100' : 'bg-zinc-800/40 text-zinc-500 hover:text-zinc-300 hover:bg-zinc-800'
            }`} data-testid={`breakdown-view-${v.key}`}>
            <v.icon className="w-3 h-3" />
            {v.label}
          </button>
        ))}
      </div>

      {/* KPI strip */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard label="Total" value={fmtCur(ledger?.total_amount)} icon={Banknote} color="text-zinc-200" />
        <StatCard label="Groups" value={groups.length} icon={PieChart} color="text-zinc-200" />
        <StatCard label="Cash Outflows" value={fmtCur(ledger?.cash_outflow_total)} icon={ArrowDownRight} color="text-rose-400" />
        <StatCard label="Entries" value={ledger?.total_entries || 0} icon={List} color="text-zinc-200" />
      </div>

      {ledger?.total_entries === 0 ? (
        <Card className="bg-zinc-900/40 border-zinc-800/40">
          <CardContent className="p-8 text-center">
            <PieChart className="w-8 h-8 text-zinc-600 mx-auto mb-3" />
            <p className="text-sm text-zinc-400">No expense data for breakdown</p>
          </CardContent>
        </Card>
      ) : (
        <>
          {/* Breakdown bars */}
          <Card className="bg-zinc-900/40 border-zinc-800/40">
            <CardContent className="p-4">
              <div className="space-y-2">
                {groups.map(g => {
                  const pct = total > 0 ? ((g.subtotal / total) * 100).toFixed(1) : 0;
                  return (
                    <div key={g.key} className="group" data-testid={`breakdown-row-${g.key}`}>
                      <div className="flex items-center justify-between mb-1">
                        <div className="flex items-center gap-2 min-w-0 flex-1">
                          <span className="text-xs text-zinc-300 truncate max-w-[200px]">{g.key}</span>
                          <span className="text-[9px] text-zinc-600">{g.count} entries</span>
                        </div>
                        <div className="flex items-center gap-3 shrink-0">
                          <span className="text-xs font-mono text-zinc-200">{fmtCur(g.subtotal)}</span>
                          <span className="text-[10px] text-zinc-500 w-12 text-right">{pct}%</span>
                        </div>
                      </div>
                      <div className="h-1.5 bg-zinc-800 rounded-full overflow-hidden">
                        <div className="h-full bg-blue-500/60 rounded-full transition-all" style={{ width: `${Math.min(pct, 100)}%` }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>

          {/* Classification breakdown (always visible) */}
          {breakdown.by_classification && Object.keys(breakdown.by_classification).length > 0 && (
            <Card className="bg-zinc-900/40 border-zinc-800/40">
              <CardHeader className="pb-2">
                <CardTitle className="text-xs text-zinc-400">By Classification Tag</CardTitle>
              </CardHeader>
              <CardContent className="pt-0">
                <div className="flex flex-wrap gap-2">
                  {Object.entries(breakdown.by_classification).map(([cls, amt]) => (
                    <div key={cls} className="flex items-center gap-2 px-3 py-2 rounded-lg border border-zinc-700/30 bg-zinc-800/30">
                      <ClassificationBadge classification={cls} />
                      <span className="text-xs font-mono text-zinc-200">{fmtCur(amt)}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════
// SUMMARY TAB (existing dashboard content)
// ═══════════════════════════════════════════════════
function SummaryTab({ dashboard, dailyPos, expenseData, expenseConfidence, dateFrom, dateTo, physicalCash, treasury, onSyncExpenses, syncing }) {
  const cls = dashboard?.classification || {};

  const sortedCogsCategories = useMemo(
    () => Object.entries(cls.cogs_by_category || {}).sort((a, b) => b[1] - a[1]),
    [cls.cogs_by_category]
  );

  const sortedOpexCategories = useMemo(
    () => Object.entries(cls.opex_by_category || {}).sort((a, b) => b[1] - a[1]),
    [cls.opex_by_category]
  );

  const dashCashPos = dashboard?.cash_position || {};
  const cash = dailyPos || {};
  const trend = dashboard?.daily_trend || [];
  const kpi = {
    ...cash,
    cash_inflows: dashCashPos.cash_inflows ?? cash.cash_inflows ?? 0,
    cash_outflows: dashCashPos.cash_outflows ?? cash.cash_outflows ?? 0,
    computed_closing_cash: dashCashPos.computed_closing_cash ?? cash.computed_closing_cash ?? 0,
    daily_cash_burn: dashCashPos.daily_cash_burn ?? cash.daily_cash_burn ?? 0,
  };
  const vStyle = getLegacyObsStyle(cash.legacy_observation_status);
  const salesInflows = dashboard?.sales_inflows || {};
  const dailySalesInflows = salesInflows.daily || [];
  const todaySalesInflows = cash.sales_inflows || {};
  const allRails = useMemo(() => {
    const keys = Object.keys(salesInflows.by_rail || {}).length > 0
      ? Object.keys(salesInflows.by_rail)
      : Object.keys(todaySalesInflows.by_rail || {});
    return keys;
  }, [salesInflows.by_rail, todaySalesInflows.by_rail]);

  const sortedRailsByValue = useMemo(() =>
    [...allRails].sort((a, b) => ((salesInflows.by_rail || {})[b] || 0) - ((salesInflows.by_rail || {})[a] || 0)),
    [allRails, salesInflows.by_rail]
  );
  const sortedRailsAlpha = useMemo(() => [...allRails].sort(), [allRails]);

  return (
    <div className="space-y-5">
      {/* Two-Truth Cash Header — Physical Cash in Hand vs Treasury Cash Position */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="two-truth-cash-header">
        {/* Card A — Physical Cash in Hand (drawer cash) */}
        <Card className="bg-emerald-950/15 border border-emerald-500/25" data-testid="physical-cash-card">
          <CardContent className="p-4">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-[10px] text-emerald-400/80 uppercase tracking-wider flex items-center gap-1.5"
                   title="Physical Cash in Hand = physical drawer cash from CASH sales and cash-ledger movement.">
                  <Banknote className="w-3 h-3" />
                  Physical Cash in Hand
                  <span className="text-[9px] text-emerald-500/60" aria-label="info">&#9432;</span>
                </p>
                <p className="text-2xl font-bold text-emerald-300 mt-1" data-testid="physical-cash-value">
                  {fmtCur(physicalCash?.closing_physical_cash ?? 0)}
                </p>
                <p className="text-[10px] text-emerald-500/70 mt-1">
                  opening + CASH sales + ledger inflows − ledger outflows
                </p>
              </div>
              <Badge variant="outline" className={`text-[9px] ${
                physicalCash?.state === 'TRUTH'
                  ? 'border-emerald-500/40 text-emerald-300'
                  : 'border-amber-500/40 text-amber-300'
              }`} data-testid="physical-cash-state">
                {physicalCash?.state || 'UNKNOWN'}
              </Badge>
            </div>
            {physicalCash?.reason_human && physicalCash.state !== 'TRUTH' && (
              <p className="text-[10px] text-amber-300/80 mt-2 border-t border-emerald-500/10 pt-2" data-testid="physical-cash-reason">
                {physicalCash.reason_human}
              </p>
            )}
            <div className="grid grid-cols-4 gap-2 mt-3 text-[10px]">
              <div><span className="text-zinc-500">Opening</span><div className="text-zinc-300 font-mono">{fmtCur(physicalCash?.opening_cash ?? 0)}</div></div>
              <div><span className="text-zinc-500">CASH sales</span><div className="text-emerald-400 font-mono">+{fmtCur(physicalCash?.cash_sales ?? 0)}</div></div>
              <div><span className="text-zinc-500">Ledger in</span><div className="text-emerald-400 font-mono">+{fmtCur(physicalCash?.cash_ledger_inflows ?? 0)}</div></div>
              <div><span className="text-zinc-500">Ledger out</span><div className="text-rose-400 font-mono">-{fmtCur(physicalCash?.cash_ledger_outflows ?? 0)}</div></div>
            </div>
          </CardContent>
        </Card>

        {/* Card B — Treasury Cash Position (pooled bank+cash+wallet) */}
        <Card className="bg-blue-950/15 border border-blue-500/25" data-testid="treasury-cash-card">
          <CardContent className="p-4">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-[10px] text-blue-400/80 uppercase tracking-wider flex items-center gap-1.5"
                   title="Treasury Cash Position = pooled realized position across cash/bank/wallet accounts. Not physical cash.">
                  <Wallet className="w-3 h-3" />
                  Treasury Cash Position
                  <span className="text-[9px] text-blue-500/60" aria-label="info">&#9432;</span>
                </p>
                <p className="text-2xl font-bold text-blue-300 mt-1" data-testid="treasury-cash-value">
                  {treasury?.totals?.treasury_cash_position != null
                    ? fmtCur(treasury.totals.treasury_cash_position)
                    : '--'}
                </p>
                <p className="text-[10px] text-blue-500/70 mt-1">
                  cash + bank + wallet openings + realized movements
                </p>
              </div>
              <Badge variant="outline" className={`text-[9px] ${
                treasury?.cash_mode === 'TREASURY'
                  ? 'border-blue-500/40 text-blue-300'
                  : 'border-amber-500/40 text-amber-300'
              }`} data-testid="treasury-cash-mode">
                {treasury?.cash_mode || 'UNKNOWN'}
              </Badge>
            </div>
            {treasury?.cash_mode_reason && treasury.cash_mode !== 'TREASURY' && (
              <p className="text-[10px] text-amber-300/80 mt-2 border-t border-blue-500/10 pt-2" data-testid="treasury-cash-reason">
                {String(treasury.cash_mode_reason).replace(/_/g, ' ')}
              </p>
            )}
            <div className="grid grid-cols-3 gap-2 mt-3 text-[10px]">
              <div><span className="text-zinc-500">Opening</span><div className="text-zinc-300 font-mono">{fmtCur(treasury?.totals?.opening_cash ?? 0)}</div></div>
              <div><span className="text-zinc-500">Realized in</span><div className="text-emerald-400 font-mono">+{fmtCur(treasury?.totals?.realized_inflow ?? 0)}</div></div>
              <div><span className="text-zinc-500">Realized out</span><div className="text-rose-400 font-mono">-{fmtCur(treasury?.totals?.realized_outflow ?? 0)}</div></div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Cash Position KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="cash-kpi-cards">
        <StatCard label="Opening Cash" value={fmtCur(kpi.opening_cash)} icon={Wallet} color="text-zinc-300"
          tooltip="Opening physical cash from EC cash ledger." testId="kpi-opening-cash" />
        <StatCard label="Cash Inflows" value={fmtCur(kpi.cash_inflows)} icon={ArrowUpRight} color="text-emerald-400"
          sub="CASH rail (payments_by_rail)"
          tooltip="Customer-paid CASH amount only — sourced from sc_transactions.payments_by_rail.CASH."
          testId="kpi-cash-inflows" />
        <StatCard label="Cash Outflows" value={fmtCur(kpi.cash_outflows)} icon={ArrowDownRight} color="text-red-400"
          tooltip="Physical cash spends from EC cash ledger. 0 when ledger has no outflow entries."
          testId="kpi-cash-outflows" />
        <StatCard label="Legacy EC Closing (observation)" value={fmtCur(kpi.reported_closing_cash)} icon={Banknote}
          color="text-zinc-300"
          sub={kpi.ledger_available ? 'EC_LEGACY_OBSERVATION — not cash truth' : 'No legacy observation'}
          tooltip="Closing figure as observed by the retiring Expense Capture cash ledger. It is NOT canonical cash truth and NOT a verified cash position — a real cash control needs a physical cash count from 1OS Bills."
          testId="kpi-legacy-ec-closing" />
        <StatCard label="Daily Cash Burn" value={fmtCur(kpi.daily_cash_burn)} icon={TrendingDown} color="text-amber-400"
          sub={`${cls.expense_count || kpi.expense_count || 0} bills`}
          testId="kpi-daily-burn" />
      </div>

      {/* Legacy EC observation — NOT a cash reconciliation */}
      <Card className={`${vStyle.bg} border`} data-testid="legacy-ec-observation-card">
        <CardContent className="p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <ShieldAlert className="w-5 h-5 text-zinc-500" />
              <div>
                <p className={`text-sm font-medium ${vStyle.color}`} data-testid="legacy-ec-observation-label">{vStyle.label}</p>
                <p className="text-[11px] text-zinc-400 mt-0.5" data-testid="legacy-ec-observation-detail">
                  {cash.ledger_available
                    ? `Legacy EC closing: ${fmtCur(cash.reported_closing_cash)} | Accounts computed: ${fmtCur(cash.computed_closing_cash)} | Difference: ${fmtCur(cash.legacy_observation_difference)}`
                    : 'No legacy Expense Capture observation for this date.'}
                </p>
                <p className="text-[10px] text-zinc-500 mt-1" data-testid="cash-reconciliation-state">
                  Not a cash reconciliation. A verified cash control needs a physical cash count from 1OS Bills — {cash.cash_reconciliation_state || 'WAITING_FOR_BILLS_PHYSICAL_COUNT'}.
                </p>
              </div>
            </div>
            {cash.ledger_available && cash.legacy_observation_difference !== 0 && (
              <Badge variant="outline" className={`text-[9px] ${vStyle.color}`} data-testid="legacy-difference-badge">
                {fmtCur(cash.legacy_observation_difference)}
              </Badge>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Expense Confidence Strip */}
      {expenseConfidence && (
        <Card className={`border ${
          expenseConfidence.expense_confidence_level === 'HIGH' ? 'bg-emerald-950/10 border-emerald-500/20' :
          expenseConfidence.expense_confidence_level === 'MEDIUM' ? 'bg-amber-950/10 border-amber-500/20' :
          'bg-red-950/10 border-red-500/20'
        }`} data-testid="expense-confidence-strip">
          <CardContent className="p-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className={`w-8 h-8 rounded-lg flex items-center justify-center text-xs font-bold ${
                  expenseConfidence.expense_confidence_level === 'HIGH' ? 'bg-emerald-500/20 text-emerald-400' :
                  expenseConfidence.expense_confidence_level === 'MEDIUM' ? 'bg-amber-500/20 text-amber-400' :
                  'bg-red-500/20 text-red-400'
                }`} data-testid="expense-confidence-score">{expenseConfidence.expense_confidence_score}</div>
                <div>
                  <p className="text-xs font-medium text-zinc-300">Expense Confidence</p>
                  <p className="text-[10px] text-zinc-500 mt-0.5">{expenseConfidence.primary_reason}</p>
                </div>
              </div>
              <div className="flex items-center gap-3 text-[10px] text-zinc-500">
                <span data-testid="expense-sync-health">Sync: {expenseConfidence.expense_sync_health}</span>
                <span data-testid="expense-coverage">Coverage: {(expenseConfidence.expense_coverage_ratio * 100).toFixed(0)}%</span>
                {expenseConfidence.hours_since_sync && (
                  <span>Last sync: {expenseConfidence.hours_since_sync < 1 ? '<1h' : `${Math.round(expenseConfidence.hours_since_sync)}h`} ago</span>
                )}
                <Badge variant="outline" className={`text-[9px] ${expenseConfidence.is_actionable ? 'border-emerald-500/30 text-emerald-400' : 'border-red-500/30 text-red-400'}`} data-testid="expense-actionable-badge">
                  {expenseConfidence.is_actionable ? 'Actionable' : 'Low Confidence'}
                </Badge>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Sales Inflows */}
      <Card className="bg-zinc-900/40 border-zinc-800/40" data-testid="sales-inflows-card">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <ArrowUpRight className="w-4 h-4 text-emerald-400" />
              <CardTitle className="text-xs text-zinc-400">Sales Inflows (from Transactions)</CardTitle>
            </div>
            <div className="flex items-center gap-1.5">
              {salesInflows.confidence === 'DIRECTIONAL' && (
                <Badge variant="outline" className="text-[9px] border-amber-500/30 text-amber-400" data-testid="sales-confidence-directional"
                  title={salesInflows.confidence_reason || 'Directional only — not truth-grade.'}>
                  Directional · {salesInflows.source}
                </Badge>
              )}
              {salesInflows.confidence === 'TRUTH' && (
                <Badge variant="outline" className="text-[9px] border-emerald-500/30 text-emerald-400" data-testid="sales-confidence-truth">
                  Truth · SC
                </Badge>
              )}
              <Badge variant="outline" className="text-[9px] border-emerald-500/20 text-emerald-400">
                {salesInflows.transaction_count || todaySalesInflows.transaction_count || 0} txns
              </Badge>
            </div>
          </div>
        </CardHeader>
        <CardContent className="pt-0 space-y-3">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            <div className="bg-zinc-800/40 rounded-lg p-2.5 border border-zinc-700/30">
              <p className="text-[10px] text-zinc-500 uppercase tracking-wider">Total Sales</p>
              <p className="text-lg font-bold text-zinc-100 mt-0.5" data-testid="total-sales-inflow">{fmtCur(salesInflows.total_sales || todaySalesInflows.total_sales || 0)}</p>
            </div>
            <div className="bg-emerald-500/5 rounded-lg p-2.5 border border-emerald-500/15">
              <p className="text-[10px] text-emerald-500 uppercase tracking-wider flex items-center gap-1"
                 title="Customer-paid CASH amount only. Source: sc_transactions.payments_by_rail.CASH. Does NOT include opening cash or cash-ledger outflows.">
                Physical Cash from Sales
                <span className="text-[9px] text-emerald-500/70" aria-label="info">&#9432;</span>
              </p>
              <p className="text-lg font-bold text-emerald-400 mt-0.5" data-testid="cash-in-hand-from-sales">{fmtCur(salesInflows.physical_cash_from_sales || salesInflows.immediate_cash || todaySalesInflows.immediate_cash || 0)}</p>
              <p className="text-[9px] text-zinc-500">payments_by_rail.CASH</p>
            </div>
            <div className="bg-blue-500/5 rounded-lg p-2.5 border border-blue-500/15">
              <p className="text-[10px] text-blue-500 uppercase tracking-wider">Digital (UPI)</p>
              <p className="text-lg font-bold text-blue-400 mt-0.5" data-testid="digital-inflow">{fmtCur(salesInflows.immediate_digital || todaySalesInflows.immediate_digital || 0)}</p>
              <p className="text-[9px] text-zinc-500">Settles same/next day</p>
            </div>
            <div className="bg-amber-500/5 rounded-lg p-2.5 border border-amber-500/15">
              <p className="text-[10px] text-amber-500 uppercase tracking-wider">Pending Settlement</p>
              <p className="text-lg font-bold text-amber-400 mt-0.5" data-testid="pending-settlement-inflow">{fmtCur(salesInflows.pending_settlement || todaySalesInflows.pending_settlement || 0)}</p>
              <p className="text-[9px] text-zinc-500">Card + Partner pay (T+2-7)</p>
            </div>
          </div>
          {allRails.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {sortedRailsByValue.map(rail => {
                const meta = getRailMeta(rail);
                const RailIcon = meta.icon;
                const amt = (salesInflows.by_rail || todaySalesInflows.by_rail || {})[rail] || 0;
                return (
                  <div key={rail} className={`flex items-center gap-2 px-3 py-2 rounded-lg border border-zinc-700/30 ${meta.bg}`} data-testid={`rail-${rail.toLowerCase()}`}>
                    <RailIcon className={`w-3.5 h-3.5 ${meta.color}`} />
                    <div>
                      <p className={`text-xs font-medium ${meta.color}`}>{meta.label}</p>
                      <p className="text-sm font-mono font-semibold text-zinc-200">{fmtCur(amt)}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Daily Sales Inflow Trend */}
      {dailySalesInflows.length > 0 && (
        <Card className="bg-zinc-900/40 border-zinc-800/40" data-testid="daily-sales-inflow-table">
          <CardHeader className="pb-2">
            <CardTitle className="text-xs text-zinc-400">Daily Sales Inflow by Rail</CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            <div className="overflow-x-auto">
              <table className="w-full text-[10px]">
                <thead>
                  <tr className="border-b border-zinc-800 text-zinc-500">
                    <th className="text-left py-2 pr-3">Date</th>
                    <th className="text-right py-2 pr-3">Total</th>
                    {sortedRailsAlpha.map(rail => (
                      <th key={rail} className="text-right py-2 pr-3">{getRailMeta(rail).label}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {dailySalesInflows.map(day => (
                    <tr key={day.date} className="border-b border-zinc-800/40 hover:bg-zinc-800/20">
                      <td className="py-2 pr-3 text-zinc-300 font-mono">{day.date}</td>
                      <td className="py-2 pr-3 text-right font-mono font-medium text-zinc-200">{fmtCur(day.total_sales)}</td>
                      {sortedRailsAlpha.map(rail => {
                        const meta = getRailMeta(rail);
                        return <td key={rail} className={`py-2 pr-3 text-right font-mono ${meta.color}`}>{fmtCur((day.by_rail || {})[rail] || 0)}</td>;
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Derived Insights */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="derived-insights">
        <Card className="bg-zinc-900/40 border-zinc-800/40">
          <CardHeader className="pb-1"><div className="flex items-center gap-2"><BarChart3 className="w-4 h-4 text-blue-400" /><CardTitle className="text-xs text-zinc-400">Cash vs Vendor Spend</CardTitle></div></CardHeader>
          <CardContent className="pt-0">
            <div className="flex items-end gap-3 mt-2">
              <div><p className="text-2xl font-bold text-blue-400">{cls.cash_vs_vendor_pct || 0}%</p><p className="text-[10px] text-zinc-500">inventory via cash</p></div>
              <div className="flex-1 space-y-1.5">
                <div className="flex items-center justify-between text-[10px]"><span className="text-zinc-500">Cash</span><span className="text-amber-400 font-mono">{fmtCur(cls.cash_inventory_total)}</span></div>
                <div className="flex items-center justify-between text-[10px]"><span className="text-zinc-500">Vendor</span><span className="text-emerald-400 font-mono">{fmtCur(cls.vendor_inventory_total)}</span></div>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-zinc-900/40 border-zinc-800/40">
          <CardHeader className="pb-1"><div className="flex items-center gap-2"><Percent className="w-4 h-4 text-amber-400" /><CardTitle className="text-xs text-zinc-400">Emergency Procurement</CardTitle></div></CardHeader>
          <CardContent className="pt-0">
            <div className="mt-2">
              <p className={`text-2xl font-bold ${(cls.emergency_procurement_ratio || 0) > 0.3 ? 'text-red-400' : (cls.emergency_procurement_ratio || 0) > 0.15 ? 'text-amber-400' : 'text-emerald-400'}`}>
                {((cls.emergency_procurement_ratio || 0) * 100).toFixed(1)}%
              </p>
              <p className="text-[10px] text-zinc-500 mt-0.5">cash purchases / total inventory</p>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-zinc-900/40 border-zinc-800/40">
          <CardHeader className="pb-1"><div className="flex items-center gap-2"><Banknote className="w-4 h-4 text-emerald-400" /><CardTitle className="text-xs text-zinc-400">COGS vs OPEX Split</CardTitle></div></CardHeader>
          <CardContent className="pt-0">
            <div className="mt-2 space-y-2">
              <div className="flex items-center justify-between"><span className="text-[10px] text-zinc-500">COGS</span><span className="text-sm font-semibold text-amber-400 font-mono">{fmtCur(cls.cogs_total)}</span></div>
              <div className="w-full bg-zinc-800 rounded-full h-1.5"><div className="bg-amber-500 h-1.5 rounded-full" style={{ width: `${cls.cogs_total && cls.opex_total ? Math.min(100, cls.cogs_total / (cls.cogs_total + cls.opex_total) * 100) : 0}%` }} /></div>
              <div className="flex items-center justify-between"><span className="text-[10px] text-zinc-500">OPEX</span><span className="text-sm font-semibold text-blue-400 font-mono">{fmtCur(cls.opex_total)}</span></div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Category Breakdown */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <Card className="bg-zinc-900/40 border-zinc-800/40">
          <CardHeader className="pb-2"><CardTitle className="text-xs text-zinc-400">COGS by Category</CardTitle></CardHeader>
          <CardContent className="pt-0">
            {sortedCogsCategories.length > 0 ? (
              <div className="space-y-1.5">{sortedCogsCategories.map(([cat, amt]) => (
                <div key={cat} className="flex items-center justify-between py-1 border-b border-zinc-800/40">
                  <span className="text-[11px] text-zinc-400">{cat}</span><span className="text-[11px] font-mono text-amber-400">{fmtCur(amt)}</span>
                </div>))}</div>
            ) : <p className="text-[11px] text-zinc-600 py-3">No COGS data</p>}
          </CardContent>
        </Card>
        <Card className="bg-zinc-900/40 border-zinc-800/40">
          <CardHeader className="pb-2"><CardTitle className="text-xs text-zinc-400">OPEX by Category</CardTitle></CardHeader>
          <CardContent className="pt-0">
            {sortedOpexCategories.length > 0 ? (
              <div className="space-y-1.5">{sortedOpexCategories.map(([cat, amt]) => (
                <div key={cat} className="flex items-center justify-between py-1 border-b border-zinc-800/40">
                  <span className="text-[11px] text-zinc-400">{cat}</span><span className="text-[11px] font-mono text-blue-400">{fmtCur(amt)}</span>
                </div>))}</div>
            ) : <p className="text-[11px] text-zinc-600 py-3">No OPEX data</p>}
          </CardContent>
        </Card>
      </div>

      {/* Daily Trend */}
      {trend.length > 0 && (
        <Card className="bg-zinc-900/40 border-zinc-800/40">
          <CardHeader className="pb-2"><CardTitle className="text-xs text-zinc-400">Daily Expense Trend</CardTitle></CardHeader>
          <CardContent className="pt-0">
            <div className="overflow-x-auto">
              <table className="w-full text-[10px]" data-testid="daily-trend-table">
                <thead><tr className="border-b border-zinc-800 text-zinc-500">
                  <th className="text-left py-2 pr-3">Date</th><th className="text-right py-2 pr-3">Total</th><th className="text-right py-2 pr-3">COGS</th>
                  <th className="text-right py-2 pr-3">OPEX</th><th className="text-right py-2 pr-3">Cash Burn</th><th className="text-right py-2">Cash Inventory</th>
                </tr></thead>
                <tbody>{trend.map(d => (
                  <tr key={d.date} className="border-b border-zinc-800/40">
                    <td className="py-2 pr-3 text-zinc-300">{d.date}</td><td className="py-2 pr-3 text-right font-mono text-zinc-200">{fmtCur(d.total)}</td>
                    <td className="py-2 pr-3 text-right font-mono text-amber-400">{fmtCur(d.cogs)}</td><td className="py-2 pr-3 text-right font-mono text-blue-400">{fmtCur(d.opex)}</td>
                    <td className="py-2 pr-3 text-right font-mono text-red-400">{fmtCur(d.cash_burn)}</td><td className="py-2 text-right font-mono text-amber-400">{fmtCur(d.cash_inventory)}</td>
                  </tr>))}</tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Expense Classification */}
      {expenseData?.available && expenseData?.classified && (
        <div className="space-y-4 mt-2" data-testid="expense-classification-section">
          <div className="flex items-center gap-2">
            <Package className="w-4 h-4 text-zinc-500" />
            <h2 className="text-sm font-semibold text-zinc-300">Expense Classification</h2>
            <Badge variant="outline" className={`text-[9px] ${expenseData.source === 'LOCAL_STORE' ? 'border-emerald-500/30 text-emerald-400' : 'border-amber-500/30 text-amber-400'}`}>
              {expenseData.source === 'LOCAL_STORE' ? 'Local Store' : 'Live Fetch'}
            </Badge>
            <span className="text-[10px] text-zinc-500">{expenseData.count} expenses</span>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="classification-kpis">
            <StatCard label="Cash Outflows" value={fmtCur(expenseData.classified.cash_impact_total)} icon={ArrowDownRight} color="text-rose-400" />
            <StatCard label="Inventory (COGS)" value={fmtCur(expenseData.classified.cogs_total)} icon={Package} color="text-amber-400" />
            <StatCard label="Inventory via Cash" value={fmtCur(expenseData.classified.cash_inventory_total)} icon={Banknote} color="text-emerald-400" />
            <StatCard label="Emergency Procurement" value={`${((expenseData.classified.emergency_procurement_ratio || 0) * 100).toFixed(1)}%`}
              icon={AlertTriangle} color={(expenseData.classified.emergency_procurement_ratio || 0) > 0.15 ? 'text-rose-400' : 'text-zinc-400'} />
          </div>
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════
// MAIN PAGE
// ═══════════════════════════════════════════════════
export default function CashDashboardPage() {
  const periodState = usePeriod(PERIOD_MODES.RANGE);
  const dateFrom = periodState.period.from;
  const dateTo = periodState.period.to;
  const [activeTab, setActiveTab] = useState('summary');
  const [dashboard, setDashboard] = useState(null);
  const [dailyPos, setDailyPos] = useState(null);
  const [physicalCash, setPhysicalCash] = useState(null);
  const [treasury, setTreasury] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expenseData, setExpenseData] = useState(null);
  const [expenseConfidence, setExpenseConfidence] = useState(null);
  const [syncing, setSyncing] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [dashRes, posRes, expRes, physRes, treasuryRes] = await Promise.all([
        accountsAPI.getCashDashboard(periodState.apiParams),
        accountsAPI.getDailyCashPosition({ date: dateTo }),
        accountsAPI.getExpensesClassified({ date_from: dateFrom, date_to: dateTo }).catch(() => ({ data: null })),
        accountsAPI.getPhysicalCashPosition({ date_from: dateFrom, date_to: dateTo }).catch(() => ({ data: null })),
        accountsAPI.getCashflowOverview({ date_from: dateFrom, date_to: dateTo }).catch(() => ({ data: null })),
      ]);
      setDashboard(dashRes.data);
      setDailyPos(posRes.data);
      setExpenseData(expRes.data);
      setPhysicalCash(physRes.data);
      setTreasury(treasuryRes.data);
      accountsAPI.getExpenseConfidence({ date_from: dateFrom, date_to: dateTo })
        .then(r => setExpenseConfidence(r.data))
        .catch(() => setExpenseConfidence(null));
    } catch {
      toast.error('Failed to load cash dashboard');
    } finally {
      setLoading(false);
    }
  }, [dateFrom, dateTo, periodState.apiParams]);

  useEffect(() => { load(); }, [load]);

  const handleSyncExpenses = async () => {
    setSyncing(true);
    try {
      const res = await accountsAPI.syncExpenses();
      const d = res.data;
      const orphanMsg = d.orphans_removed > 0 ? `, ${d.orphans_removed} orphaned removed` : '';
      toast.success(`Synced ${d.synced || 0} new, ${d.updated || 0} updated from ${d.fetched || 0} expenses${orphanMsg}`);
      load();
    } catch {
      toast.error('Expense sync failed');
    }
    setSyncing(false);
  };

  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-100 p-4 md:p-6 max-w-7xl mx-auto space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-xl font-bold text-zinc-100" data-testid="cash-dashboard-title">Cash Dashboard</h1>
          <p className="text-xs text-zinc-500 mt-0.5">Cash position &middot; Expense ledger &middot; Classification breakdown</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <PeriodToolbar state={periodState} mode={PERIOD_MODES.RANGE} resolved={dashboard} />
          <Badge variant="outline" className={`text-[9px] ${
            (dashboard?.expense_source) === 'EXPENSE_ENGINE' ? 'border-emerald-500/30 text-emerald-400' :
            (dashboard?.expense_source) === 'EXPENSE_ENGINE_BILLS' ? 'border-blue-500/30 text-blue-400' :
            'border-amber-500/30 text-amber-400'
          }`}>
            {(dashboard?.expense_source) === 'EXPENSE_ENGINE' ? 'Live Expense Engine' :
             (dashboard?.expense_source) === 'EXPENSE_ENGINE_BILLS' ? 'Expense Engine Bills' :
             'Bills Fallback'}
          </Badge>
          <Button variant="outline" size="sm" className="h-7 text-xs gap-1.5 border-zinc-700" onClick={handleSyncExpenses} disabled={syncing} data-testid="sync-expenses-btn">
            {syncing ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
            {syncing ? 'Syncing...' : 'Sync Expenses'}
          </Button>
        </div>
      </div>

      {/* Tab Bar */}
      <div className="flex gap-1 bg-zinc-900/60 rounded-lg p-1 w-fit border border-zinc-800/50" data-testid="cash-tab-bar">
        {TABS.map(tab => (
          <button key={tab.key} onClick={() => setActiveTab(tab.key)}
            className={`flex items-center gap-1.5 px-4 py-2 rounded-md text-xs font-medium transition-all ${
              activeTab === tab.key
                ? 'bg-zinc-800 text-zinc-100 shadow-sm'
                : 'text-zinc-500 hover:text-zinc-300 hover:bg-zinc-800/40'
            }`} data-testid={`tab-${tab.key}`}>
            <tab.icon className="w-3.5 h-3.5" />
            {tab.label}
          </button>
        ))}
      </div>

      {loading && activeTab === 'summary' ? (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {[1,2,3,4].map(i => <Card key={i} className="bg-zinc-900/40 border-zinc-800/40"><CardContent className="p-3"><div className="h-12 bg-zinc-800/50 rounded animate-pulse" /></CardContent></Card>)}
        </div>
      ) : (
        <>
          {activeTab === 'summary' && (
            <SummaryTab
              dashboard={dashboard} dailyPos={dailyPos} expenseData={expenseData}
              expenseConfidence={expenseConfidence} dateFrom={dateFrom} dateTo={dateTo}
              physicalCash={physicalCash} treasury={treasury}
              onSyncExpenses={handleSyncExpenses} syncing={syncing}
            />
          )}
          {activeTab === 'ledger' && <LedgerTab dateFrom={dateFrom} dateTo={dateTo} />}
          {activeTab === 'breakdown' && <BreakdownTab dateFrom={dateFrom} dateTo={dateTo} />}
        </>
      )}
    </div>
  );
}
